/* © CNES */
import { FileUpload, FileUploadHandlerEvent } from 'primereact/fileupload';
import { Toast } from 'primereact/toast';
import { useCallback, useContext, useMemo, useRef } from 'react';
import { ConfigContext } from '../../../../../../context/ConfigContext';
import './ButtonUpload.scss';
import { readGeoJsonFile, readKMLFile, readShapeFile } from './Utils';

/**
 * ButtonUpload displays a button to upload file.
 * @param uploadFileExtention uploadFile Extention to handle uploadFile actions.
 * @returns ButtonUpload Element.
 */
export const ButtonUpload = ({
  uploadFileExtention,
}: {
  uploadFileExtention: string;
}) => {
  const toast = useRef<Toast>(null);
  const context = useContext(ConfigContext);

  /**
   * function called to display toast component
   */
  const displayToast = useCallback(
    (severity: 'error', summary: string, detail: string) => {
      toast.current?.show({
        severity: severity,
        summary: summary,
        detail: detail,
        life: 2000,
      });
    },
    []
  );

  /**
   * handle on upload GeoJSON File.
   * @param event file upload event
   */
  const onUploadFile = async (event: FileUploadHandlerEvent) => {
    const file: File = event.files[0];

    if (file) {
      if (uploadFileExtention === 'GeoJSON') {
        readGeoJsonFile(file)
          .then((geoJson) => {
            context.setSearchZone({ type: 'GeoJSON file', value: geoJson });
          })
          .catch((error: Error) => {
            displayToast('error', 'Cannot upload file', error.message);
          })
          .finally(() => event.options.clear());
      } else if (uploadFileExtention === 'SHP') {
        readShapeFile(file)
          .then((geoJson) => {
            context.setSearchZone({ type: 'SHP file', value: geoJson });
          })
          .catch((error: Error) => {
            displayToast('error', 'Cannot upload file', error.message);
          })
          .finally(() => event.options.clear());
      } else if (uploadFileExtention === 'KML') {
        readKMLFile(file)
          .then((kml) => {
            context.setSearchZone({ type: 'KML file', value: kml });
          })
          .catch((error: Error) => {
            displayToast('error', 'Cannot upload file', error.message);
          })
          .finally(() => event.options.clear());
      }
    }
  };

  const acceptedExtension = useMemo(() => {
    return uploadFileExtention === 'SHP' ? '.zip' : `.${uploadFileExtention}`;
  }, [uploadFileExtention]);

  return (
    <div className="button-upload">
      <Toast ref={toast} position="top-center" />
      <FileUpload
        chooseLabel="Upload"
        chooseOptions={{ icon: 'pi pi-upload' }}
        mode="basic"
        name="upload"
        accept={acceptedExtension}
        customUpload
        auto
        maxFileSize={5000000}
        uploadHandler={onUploadFile}
        onValidationFail={() => {
          displayToast('error', 'File size', 'Maximum upload size is 5 Mo');
        }}
      />
    </div>
  );
};
